import { useEffect, useState } from 'react';
import fetchAPI from '../helper/axios';
import { CMS_API_BASE, CUS_API_BASE, IsDevMode } from '../helper/constants';
const API_BASE = CMS_API_BASE;
const CustAPI_BASE = CUS_API_BASE;
const cache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000;

const fetchWithCache = async (key: string, fn: () => Promise<any>) => {
  const now = Date.now();
  const cached = cache.get(key);
  if (cached && now - cached.timestamp < CACHE_DURATION) return cached.data;
  const data = await fn();
  cache.set(key, { data, timestamp: now });
  return data;
};

export const useContactData = () => {
  const [pageData, setPageData] = useState<any>(null);
  const [heroImg, setHeroImg] = useState<any>(null);
  const [mobheroImg, setmobHeroImg] = useState<any>(null);
  const [offices, setOffices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let isMounted = true;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const load = async () => {
      try {
        setLoading(true);

        const [page, hero, mobhero, officeResp] = await Promise.all([
          fetchWithCache('contact-node-39', () => fetchAPI('node/39')),
          fetchWithCache('contact-media-48', () => fetchAPI('media/48/edit')),
          fetchWithCache('contact-media-136', () => fetchAPI('media/136/edit')),
          fetchWithCache('contact-offices', async () => {
            const response =
              IsDevMode === 1
                ? await fetch('/api/jsonapi/node/connect_with_us_contact_page')
                : await fetch(API_BASE + '/jsonapi/node/connect_with_us_contact_page');

            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
          }),
        ]);

        if (!isMounted) return;

        const mapped = officeResp.data.map((item: any) => ({
          field_contact_office_one: item.attributes.field_contact_ofiice_one ?? "",
          field_contact_office_one_descrip:
            item.attributes.field_contact_ofiice_one_descrip?.value ?? "",
          field_contact_office_second:
            item.attributes.field_contact_office_second ?? "",
          field_contact_office_second_desc:
            item.attributes.field_contact_ofiice_second_desc?.value ?? "",
          field_contact_telephone:
            item.attributes.field_contact_telephone?.value ?? "",
          field_email: item.attributes.field_email ?? "",
        }));

        setPageData(page);
        setHeroImg(hero);
        setmobHeroImg(mobhero);
        setOffices(mapped);
        setError(null);
      } catch (e: any) {
        if (!isMounted) return;

        setError(e.message || "Failed to load contact data");

        // ✅ retry max 3 times only
        if (retry < 3) {
          retryTimer = setTimeout(() => {
            setRetry((prev) => prev + 1);
          }, 5000);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();

    return () => {
      isMounted = false;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [retry]);

  return { pageData, heroImg, mobheroImg, offices, loading, error };
};
